import { frontendErrorsFormat } from "@/features/observability/components/frontend-errors-format";
import type {
    FrontendErrorsCompleteness,
    FrontendErrorsSeriesBucket,
} from "@/features/observability/types/frontend-errors.types";

// Buckets the reader answered without a category breakdown are stacked under this one key.
export const SERIES_TOTAL_KEY = "__total";

export type FrontendErrorsSeriesRow = {
    start: number;
    end: number;
    count: number | null;
    devices: number | null;
    gap: boolean;
    values: Record<string, number | null>;
};

export type FrontendErrorsSeriesModel = {
    keys: string[];
    rows: FrontendErrorsSeriesRow[];
    hasGaps: boolean;
};

const keysOf = (buckets: FrontendErrorsSeriesBucket[]): string[] => {
    const seen: string[] = [];
    const add = (key: string): void => {
        if (!seen.includes(key)) seen.push(key);
    };
    frontendErrorsFormat.CATEGORIES.forEach((category) => {
        if (buckets.some((bucket) => bucket.byCategory !== null && bucket.byCategory.some((entry) => entry.category === category))) {
            add(category);
        }
    });
    buckets.forEach((bucket) => {
        if (bucket.byCategory === null) {
            if (bucket.count !== null) add(SERIES_TOTAL_KEY);
            return;
        }
        bucket.byCategory.forEach((entry) => add(entry.category));
    });
    return seen;
};

// A null count stays null in every stacked value: the chart must draw an empty bucket, never a zero bar.
const valuesOf = (bucket: FrontendErrorsSeriesBucket, keys: string[]): Record<string, number | null> => {
    const values: Record<string, number | null> = {};
    keys.forEach((key) => {
        if (bucket.count === null) {
            values[key] = null;
            return;
        }
        if (bucket.byCategory === null) {
            values[key] = key === SERIES_TOTAL_KEY ? bucket.count : null;
            return;
        }
        const entry = bucket.byCategory.find((candidate) => candidate.category === key);
        values[key] = entry === undefined ? null : entry.count;
    });
    return values;
};

const toModel = (buckets: FrontendErrorsSeriesBucket[], completeness: FrontendErrorsCompleteness | null): FrontendErrorsSeriesModel => {
    const gaps = completeness !== null && completeness.status === "gaps_reported" ? completeness.gaps : [];
    const keys = keysOf(buckets);
    const rows = buckets.map((bucket) => ({
        start: bucket.start,
        end: bucket.end,
        count: bucket.count,
        devices: bucket.devices,
        gap: gaps.some((gap) => gap.from < bucket.end && gap.to > bucket.start),
        values: valuesOf(bucket, keys),
    }));
    return { keys, rows, hasGaps: rows.some((row) => row.gap) };
};

const keyStyle = (key: string): { label: string; color: string } =>
    key === SERIES_TOTAL_KEY ? { label: "Ocurrencias", color: "#1CAFFF" } : frontendErrorsFormat.categoryStyle(key);

export const frontendErrorsSeriesModel = {
    toModel,
    keyStyle,
};
